import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Header } from '@/components/Header';
import { Footer } from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { 
  Clock, Calendar, Video, Music, CheckCircle2, Loader2,
  Package, User, Mail, ArrowRight
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

export const ViewPackage = () => {
  const { packageId } = useParams();
  const [packageData, setPackageData] = useState(null);
  const [studentName, setStudentName] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchPackage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [packageId]);

  const fetchPackage = async () => {
    try {
      const response = await axios.get(`${API}/view-package/${packageId}`);
      setPackageData(response.data.package);
      setStudentName(response.data.student_name);
    } catch (error) {
      console.error('Error fetching package:', error);
      setError('Package not found or has expired.');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <Loader2 className="animate-spin text-[#D4AF37]" size={40} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#050505]">
        <Header />
        <main className="pt-24 pb-16 flex items-center justify-center">
          <div className="text-center">
            <Package size={60} className="mx-auto mb-4 text-[#A1A1AA] opacity-50" />
            <h1 className="font-heading text-2xl text-[#EDEDED] mb-2">Package Not Found</h1>
            <p className="text-[#A1A1AA] mb-6">{error}</p>
            <Link to="/">
              <Button className="btn-gold">Go to Homepage</Button>
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const totalSessions = packageData.class_allocations?.reduce(
    (sum, alloc) => sum + (alloc.sessions_per_month * packageData.semester_months), 0
  ) || 0;

  return (
    <div className="min-h-screen bg-[#050505]">
      <Header />
      
      <main className="pt-24 pb-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="text-center mb-12">
            <p className="font-accent text-[#D4AF37] text-sm tracking-[0.2em] uppercase mb-4">
              Your Custom Plan
            </p>
            <h1 className="font-heading text-4xl md:text-5xl font-bold text-[#EDEDED] mb-4">
              Hi {studentName}!
            </h1>
            <p className="text-[#A1A1AA] max-w-2xl mx-auto">
              Based on what you told us, here's your personalized semester plan. 
              Take a look and let us know if you'd like any changes.
            </p>
          </div>

          {/* Package Overview */}
          <div className="glass-card rounded-2xl p-8 mb-8">
            <div className="grid md:grid-cols-3 gap-6 text-center">
              <div>
                <div className="w-16 h-16 rounded-full bg-[#D4AF37]/10 flex items-center justify-center mx-auto mb-3">
                  <Clock className="text-[#D4AF37]" size={28} />
                </div>
                <p className="font-heading text-3xl text-[#D4AF37]">{packageData.total_hours}</p>
                <p className="text-[#A1A1AA] text-sm">Total Hours</p>
              </div>
              
              <div>
                <div className="w-16 h-16 rounded-full bg-[#D4AF37]/10 flex items-center justify-center mx-auto mb-3">
                  <Calendar className="text-[#D4AF37]" size={28} />
                </div>
                <p className="font-heading text-3xl text-[#D4AF37]">{packageData.semester_months}</p>
                <p className="text-[#A1A1AA] text-sm">Months</p>
              </div>
              
              <div>
                <div className="w-16 h-16 rounded-full bg-[#D4AF37]/10 flex items-center justify-center mx-auto mb-3">
                  <Music className="text-[#D4AF37]" size={28} />
                </div>
                <p className="font-heading text-3xl text-[#D4AF37]">{packageData.class_allocations?.length || 0}</p>
                <p className="text-[#A1A1AA] text-sm">Classes</p>
              </div>
            </div>
          </div>

          {/* Classes */}
          <div className="glass-card rounded-2xl p-8 mb-8">
            <h2 className="font-heading text-2xl text-[#F5F5DC] mb-6">Your Classes</h2>
            
            <div className="space-y-4">
              {packageData.class_allocations?.map((alloc, index) => (
                <div 
                  key={index}
                  className="border border-white/10 rounded-xl p-6 hover:border-[#D4AF37]/30 transition-colors"
                >
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    <div>
                      <p className="text-xs text-[#D4AF37] mb-1">{alloc.classroom_name}</p>
                      <h3 className="font-heading text-xl text-[#F5F5DC]">{alloc.class_name}</h3>
                    </div>
                    
                    <div className="flex flex-wrap gap-4">
                      <div className="flex items-center gap-2 text-[#A1A1AA]">
                        <Clock size={16} className="text-[#D4AF37]" />
                        <span>{alloc.hours_allocated} hours total</span>
                      </div>
                      
                      <div className="flex items-center gap-2 text-[#A1A1AA]">
                        <Calendar size={16} className="text-[#D4AF37]" />
                        <span>{alloc.sessions_per_month}x per month</span>
                      </div>
                      
                      <div className="flex items-center gap-2 text-[#A1A1AA]">
                        <Video size={16} className="text-[#D4AF37]" />
                        <span className="capitalize">{alloc.delivery_mode}</span>
                      </div>
                    </div>
                  </div>
                  
                  {alloc.notes && (
                    <p className="mt-3 text-[#A1A1AA] text-sm border-t border-white/10 pt-3">
                      {alloc.notes}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Notes */}
          {packageData.notes && (
            <div className="glass-card rounded-2xl p-8 mb-8">
              <h2 className="font-heading text-xl text-[#F5F5DC] mb-4">Notes from Your Instructor</h2>
              <p className="text-[#A1A1AA] whitespace-pre-wrap">{packageData.notes}</p>
            </div>
          )}

          {/* CTA */}
          <div className="text-center">
            <p className="text-[#A1A1AA] mb-6">
              Ready to get started? Have questions about your plan?
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a href="mailto:hello@nathanielmusic.com?subject=About My Semester Plan">
                <Button className="btn-gold rounded-full px-8 py-3">
                  Get in Touch
                  <ArrowRight className="ml-2" size={18} />
                </Button>
              </a>
              <Link to="/">
                <Button variant="outline" className="rounded-full px-8 py-3 border-white/20 text-[#EDEDED]">
                  Visit Homepage
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default ViewPackage;
